/**
 * Core MSAL auth provider implementation for React Admin.
 *
 * Handles the full authentication lifecycle with Microsoft Entra (Azure AD):
 * login/logout (redirect or popup), silent token renewal, and session management.
 *
 * Permissions flow:
 *   1. After login, calls the backend `/auth/me` endpoint
 *   2. Backend returns `effective_permissions` (computed server-side from the
 *      user's assigned roles)
 *   3. Permissions are transformed into React Admin's RBAC format
 *      `{ resource: { action: true } }` and cached in sessionStorage
 *   4. `getPermissions()` returns the cached permissions to React Admin
 *
 * This file is not imported directly by application code — use one of the
 * factory wrappers instead:
 *   - authProviderInstance.ts        — static singleton (env-var config)
 *   - dynamicAuthProviderInstance.ts — per-partner config from backend
 */
import { AuthProvider, HttpError, UserIdentity } from "react-admin";
import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  AccountInfo,
  SilentRequest,
  RedirectRequest,
  PopupRequest,
  AuthenticationResult,
  Configuration,
  LogLevel,
} from "@azure/msal-browser";
import { ValidAny } from "@/types";
import { API_CONSTANTS, AUTH_CONSTANTS } from "@/constants";
import { pickBranding, writeBrandingCache } from "@/utils/brandingSync";
import { isAllowedApiOrigin } from "@/config/apiOrigin";
import { createCheckError } from "@aegle/ra-commons";
import { notifyImperative } from "@/providers/notify/imperativeNotify";
import { parseRichError } from "@/utils/errors/richError";

// Storage keys shorthand
const STORAGE_KEYS = AUTH_CONSTANTS.STORAGE_KEYS;

// Backend to React Admin action mapping
// React Admin uses 'edit' instead of 'update'
const BACKEND_TO_RA_ACTIONS: Record<string, string> = {
  update: "edit",
};

// MSAL Configuration Types
export interface MsalConfig {
  clientId: string;
  authority?: string;
  redirectUri?: string;
  postLogoutRedirectUri?: string;
  scopes?: string[];
  defaultScopes?: string[];
  loginMethod?: "popup" | "redirect";
  useOIDC?: boolean;
  apiBaseUrl?: string;
}

// Default configuration values
const defaultConfig: Partial<MsalConfig> = {
  authority: "https://login.microsoftonline.com/common",
  redirectUri: window.location.origin,
  postLogoutRedirectUri: window.location.origin,
  scopes: ["User.Read"],
  loginMethod: "redirect",
  useOIDC: true,
};

/**
 * Creates an MSAL-based AuthProvider for react-admin with RBAC support
 * @param config - MSAL configuration object
 * @returns AuthProvider compatible with react-admin
 */
export const createMsalAuthProvider = (config: MsalConfig): AuthProvider => {
  const finalConfig = { ...defaultConfig, ...config };

  // Use the partner-specific API URL only after validating its origin (#5097).
  // This value can originate from PARTNER_CONFIG in sessionStorage, so an
  // unvalidated host here is a token-exfiltration sink (the bearer token is
  // attached to /auth/me and /partners/{id} below). Fall back to the build-time
  // env var when it fails the allowlist.
  let apiBaseUrl = import.meta.env.VITE_API_SERVER_URL;
  if (finalConfig.apiBaseUrl) {
    if (isAllowedApiOrigin(finalConfig.apiBaseUrl)) {
      apiBaseUrl = finalConfig.apiBaseUrl;
    } else {
      console.warn(
        `[AuthProvider] Partner apiBaseUrl failed origin validation; falling back to VITE_API_SERVER_URL. Rejected: ${finalConfig.apiBaseUrl}`
      );
    }
  }

  // Prefer scopes passed by the caller (e.g. from dynamic partner resolution)
  // over the build-time env var, which is scoped to the shared app registration.
  const callerProvidedScopes =
    finalConfig.scopes && finalConfig.scopes.length > 0 && finalConfig.scopes[0] !== "User.Read";
  const oidcDefaultScopes = callerProvidedScopes
    ? finalConfig.scopes!
    : import.meta.env.VITE_OIDC_OPTIONS_DEFAULT_SCOPES?.split(",").map((s: string) => s.trim()) || [];

  // Detect if we're using CIAM based on the authority
  const isCIAM = finalConfig.authority?.includes("ciamlogin.com") || finalConfig.authority?.includes("b2clogin.com");

  // Filter out Microsoft Graph scopes if using CIAM
  const effectiveScopes = isCIAM
    ? oidcDefaultScopes.filter((scope: string) => !scope.startsWith("User.") && !scope.includes("graph.microsoft.com"))
    : oidcDefaultScopes;

  console.log("[AuthProvider] Creating MSAL AuthProvider with config:", {
    clientId: finalConfig.clientId,
    authority: finalConfig.authority,
    redirectUri: finalConfig.redirectUri,
    apiBaseUrl,
    loginMethod: finalConfig.loginMethod,
    isCIAM,
    configScopes: finalConfig.scopes,
    callerProvidedScopes,
    originalScopes: oidcDefaultScopes,
    effectiveScopes,
    useOIDC: finalConfig.useOIDC,
  });

  // Build MSAL configuration based on OIDC settings
  const msalConfiguration: Configuration = {
    auth: {
      clientId: finalConfig.clientId,
      authority: finalConfig.authority,
      redirectUri: finalConfig.redirectUri,
      postLogoutRedirectUri: finalConfig.postLogoutRedirectUri,
      navigateToLoginRequestUrl: false,
      ...(isCIAM && {
        knownAuthorities: [new URL(finalConfig.authority!).hostname],
      }),
      // Only add OIDCOptions for non-CIAM (regular Azure AD)
      ...(!isCIAM &&
        finalConfig.useOIDC &&
        effectiveScopes.length > 0 && {
          OIDCOptions: {
            defaultScopes: effectiveScopes,
          },
        }),
    },
    cache: {
      cacheLocation: "sessionStorage",
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return;
          switch (level) {
            case LogLevel.Error:
              console.error(message);
              break;
            case LogLevel.Warning:
              console.warn(message);
              break;
            case LogLevel.Info:
              console.info(message);
              break;
            case LogLevel.Verbose:
              console.debug(message);
              break;
          }
        },
        piiLoggingEnabled: false,
        logLevel: LogLevel.Info, // Add more logging during development
      },
      windowHashTimeout: 60000,
      iframeHashTimeout: 10000,
      loadFrameTimeout: 0,
      asyncPopups: false,
    },
  };

  // Initialize MSAL instance
  const msalInstance = new PublicClientApplication(msalConfiguration);

  // Track initialization state
  let isInitialized = false;
  let initializationPromise: Promise<void> | null = null;

  // Helper function to clear all auth session data
  const clearAuthSession = () => {
    sessionStorage.removeItem(STORAGE_KEYS.AUTH_ME_DATA);
    sessionStorage.removeItem(STORAGE_KEYS.RA_PERMISSIONS);
    sessionStorage.removeItem(STORAGE_KEYS.PARTNER_BRANDING);
    sessionStorage.removeItem(STORAGE_KEYS.ACTIVE_PARTNER_ID);
    // Pinned-document choices are persisted in localStorage per record
    // (usePinnedDocument, key `coronis:pinned-doc:{type}:{id}`). A claimant id is a
    // natural-person identifier, so it must not survive logout on a shared
    // workstation (#6506 PHI). Clear the whole prefix here since MSAL only manages
    // its own cache and clearAuthSession otherwise touches sessionStorage only.
    try {
      const pinnedKeys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith("coronis:pinned-doc:")) pinnedKeys.push(key);
      }
      pinnedKeys.forEach((key) => localStorage.removeItem(key));
    } catch {
      // localStorage can throw in privacy modes — best-effort cleanup only.
    }
    // Note: Don't clear MSAL's own cache here - let logout handle that
    console.log("[AuthProvider] Session data cleared");
  };

  // Helper function to call /auth/me
  const fetchAuthMeData = async (account: AccountInfo): Promise<void> => {
    try {
      const tokenResponse = await msalInstance.acquireTokenSilent({
        scopes: [`api://${finalConfig.clientId}/coronis.api`],
        account: account,
      });

      const response = await fetch(`${apiBaseUrl}/auth/me`, {
        headers: {
          Authorization: `Bearer ${tokenResponse.accessToken}`,
        },
      });

      // Handle access denied (user not provisioned in Coronis)
      if (response.status === API_CONSTANTS.STATUS_CODES.FORBIDDEN.CODE) {
        console.warn("[AuthProvider] User not provisioned in Coronis");
        const errorData = await response.json().catch(() => ({}));

        // Clear any existing session data
        clearAuthSession();

        // Log out of Microsoft and redirect to login with error
        await msalInstance.logoutRedirect({
          postLogoutRedirectUri: `${window.location.origin}/?error=${AUTH_CONSTANTS.ERRORS.ACCESS_DENIED}&message=${encodeURIComponent(
            errorData.message || "You don't have access to Coronis."
          )}`,
        });

        // This won't be reached due to redirect, but TypeScript likes it
        throw new Error(errorData.message || "Access denied");
      }

      // Handle other error responses
      if (!response.ok) {
        console.error("[AuthProvider] /auth/me returned error:", response.status);
        throw new Error(`Failed to fetch user profile: ${response.status}`);
      }

      // Success - store user data and permissions
      const authData = await response.json();
      sessionStorage.setItem(STORAGE_KEYS.AUTH_ME_DATA, JSON.stringify(authData));
      console.log("[AuthProvider] User profile loaded:", authData.user?.email);

      // Convert backend permissions to React Admin RBAC format
      // Values are scope strings ("partner" | "assigned") instead of booleans.
      // Both are truthy, so existing hasPermission checks continue to work.
      const permissions = authData.user?.effective_permissions || [];
      const raPermissions: Record<string, Record<string, string | boolean>> = {};

      permissions.forEach((perm: { resource: string; action: string; scope?: string }) => {
        if (!raPermissions[perm.resource]) {
          raPermissions[perm.resource] = {};
        }
        const action = BACKEND_TO_RA_ACTIONS[perm.action] ?? perm.action;
        raPermissions[perm.resource][action] = perm.scope || "partner";
      });

      sessionStorage.setItem(STORAGE_KEYS.RA_PERMISSIONS, JSON.stringify(raPermissions));
      console.log("[AuthProvider] Stored backend permissions:", raPermissions);

      // Fetch and cache partner branding — but only if the user hasn't switched
      // to a different partner via the switcher. The switcher caches the active
      // partner's branding before reload; overwriting it here with the home
      // partner's branding would cause a flash of wrong colors.
      const partnerId = authData.user?.partner_id_ref;
      const existingActivePartner = sessionStorage.getItem(STORAGE_KEYS.ACTIVE_PARTNER_ID);
      const hasBrandingCache = sessionStorage.getItem(STORAGE_KEYS.PARTNER_BRANDING);
      const shouldFetchBranding = partnerId && (!existingActivePartner || !hasBrandingCache);

      if (shouldFetchBranding) {
        try {
          const partnerResponse = await fetch(`${apiBaseUrl}/partners/${partnerId}`, {
            headers: {
              Authorization: `Bearer ${tokenResponse.accessToken}`,
            },
          });

          if (partnerResponse.ok) {
            const partner = await partnerResponse.json();
            // Canonical dedicated branding column first; configuration.branding is the
            // legacy fallback being retired (#4116 / #4758).
            const branding = pickBranding(partner);
            if (
              writeBrandingCache({
                branding,
                logo_image: partner.logo_image,
                logo_image_dark: partner.logo_image_dark,
                partner_company_name: partner.partner_company_name,
              })
            ) {
              console.log("[AuthProvider] Partner branding cached for:", partner.partner_company_name);
            }
          }
        } catch (error) {
          console.warn("[AuthProvider] Failed to fetch partner branding:", error);
          // Non-fatal — app will fall back to default theme
        }
      } else if (partnerId && existingActivePartner) {
        console.log(
          "[AuthProvider] Skipping home partner branding fetch — active partner override exists:",
          existingActivePartner
        );
      }

      if (partnerId) {
        // Auto-select home partner context if not already overridden (e.g. by SuperAdmin switcher)
        if (!existingActivePartner) {
          sessionStorage.setItem(STORAGE_KEYS.ACTIVE_PARTNER_ID, partnerId);
          console.log("[AuthProvider] Auto-selected home partner:", partnerId);
        }
      }
    } catch (error) {
      console.error("[AuthProvider] Failed to fetch /auth/me:", error);
      throw error; // Re-throw so callers can handle it
    }
  };

  // Initialize MSAL instance
  // Initialize MSAL instance
  const ensureInitialized = async () => {
    if (isInitialized) {
      console.log("[MSAL] Already initialized");
      return;
    }

    if (!initializationPromise) {
      console.log("[MSAL] Starting initialization...");
      initializationPromise = msalInstance.initialize().then(async () => {
        isInitialized = true;
        console.log("[MSAL] Initialization complete");

        // Handle redirect after initialization if using redirect flow
        if (finalConfig.loginMethod === "redirect") {
          try {
            console.log("[MSAL] Handling redirect promise...");
            const response = await msalInstance.handleRedirectPromise();
            console.log("[MSAL] Redirect response:", response);

            if (response && response.account) {
              console.log("[MSAL] Setting active account from redirect:", response.account);
              msalInstance.setActiveAccount(response.account);

              // Fetch backend user data and effective permissions from /auth/me
              await fetchAuthMeData(response.account);
            } else {
              // handleRedirectPromise() returned null — either no redirect occurred,
              // or MSAL v4 already consumed the redirect response during initialize().
              const accounts = msalInstance.getAllAccounts();
              console.log("[MSAL] All accounts:", accounts);
              if (accounts.length > 0) {
                console.log("[MSAL] Setting first account as active:", accounts[0]);
                msalInstance.setActiveAccount(accounts[0]);

                // If session data is missing, fetch it now. This handles the case where
                // MSAL v4's initialize() consumed the redirect response before our code
                // could call fetchAuthMeData(). Without this, the first login after a
                // redirect leaves the user with no permissions or partner context.
                const hasAuthMeData = sessionStorage.getItem(STORAGE_KEYS.AUTH_ME_DATA);
                if (!hasAuthMeData) {
                  console.log("[MSAL] No session data — fetching /auth/me for existing account");
                  await fetchAuthMeData(accounts[0]);
                } else {
                  // Session data already exists (page reload) — just backfill any gaps

                  // Backfill active partner context
                  try {
                    const data = JSON.parse(hasAuthMeData);
                    const homePartnerId = data.user?.partner_id_ref;
                    if (homePartnerId && !sessionStorage.getItem(STORAGE_KEYS.ACTIVE_PARTNER_ID)) {
                      sessionStorage.setItem(STORAGE_KEYS.ACTIVE_PARTNER_ID, homePartnerId);
                      console.log("[MSAL] Backfilled active partner from home partner:", homePartnerId);
                    }
                  } catch {
                    // Non-fatal
                  }

                  // Backfill partner branding cache if missing
                  const hasBrandingCache = sessionStorage.getItem(STORAGE_KEYS.PARTNER_BRANDING);
                  if (!hasBrandingCache) {
                    try {
                      const data = JSON.parse(hasAuthMeData);
                      const partnerId = data.user?.partner_id_ref;
                      if (partnerId) {
                        const tokenResponse = await msalInstance.acquireTokenSilent({
                          scopes: [`api://${finalConfig.clientId}/coronis.api`],
                          account: accounts[0],
                        });
                        const partnerResponse = await fetch(`${apiBaseUrl}/partners/${partnerId}`, {
                          headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
                        });
                        if (partnerResponse.ok) {
                          const partner = await partnerResponse.json();
                          // Canonical dedicated branding column first (#4116 / #4758).
                          const branding = pickBranding(partner);
                          if (
                            writeBrandingCache({
                              branding,
                              logo_image: partner.logo_image,
                              logo_image_dark: partner.logo_image_dark,
                              partner_company_name: partner.partner_company_name,
                            })
                          ) {
                            console.log("[MSAL] Backfilled partner branding cache");
                          }
                        }
                      }
                    } catch (error) {
                      console.warn("[MSAL] Failed to backfill partner branding:", error);
                    }
                  }
                }
              }
            }
            // Restore deep link after successful auth redirect
            const savedRedirectUrl = sessionStorage.getItem(STORAGE_KEYS.AUTH_REDIRECT_URL);
            if (savedRedirectUrl && msalInstance.getActiveAccount()) {
              sessionStorage.removeItem(STORAGE_KEYS.AUTH_REDIRECT_URL);
              console.log("[MSAL] Restoring deep link after auth:", savedRedirectUrl);
              // Defer navigation so React Admin finishes mounting before we change the hash
              setTimeout(() => {
                window.location.hash = savedRedirectUrl.replace(/^#/, "");
              }, 0);
            }
          } catch (error) {
            console.error("[MSAL] Error handling redirect promise:", error);
          }
        }
      });
    }

    return initializationPromise;
  };

  // Helper function to get the current account
  const getCurrentAccount = (): AccountInfo | null => {
    const activeAccount = msalInstance.getActiveAccount();
    console.log("[MSAL] Active account:", activeAccount);

    if (activeAccount) return activeAccount;

    const accounts = msalInstance.getAllAccounts();
    console.log("[MSAL] All accounts:", accounts);

    if (accounts.length > 0) {
      console.log("[MSAL] Setting first account as active");
      msalInstance.setActiveAccount(accounts[0]);
      return accounts[0];
    }
    return null;
  };

  // Helper function to acquire token silently.
  // `forceRefresh` bypasses MSAL's cached token — required for the checkError path:
  // if the server rejects a token MSAL still considers valid (revocation, key
  // rotation), a non-forced refresh returns the same cached token and the user
  // loops on 401 with no logout. Forcing a refresh either yields a genuinely new
  // token or fails, which then triggers the logout/redirect branch.
  const acquireTokenSilent = async (forceRefresh = false): Promise<AuthenticationResult> => {
    const account = getCurrentAccount();
    if (!account) {
      console.error("[MSAL] acquireTokenSilent: No active account");
      throw new Error("No active account");
    }

    const requestScopes = effectiveScopes.length > 0 ? effectiveScopes : finalConfig.scopes!;

    const request: SilentRequest = {
      scopes: requestScopes,
      account: account,
      forceRefresh,
    };

    try {
      const result = await msalInstance.acquireTokenSilent(request);
      console.log("[MSAL] Token acquired silently");
      return result;
    } catch (error) {
      console.error("[MSAL] Silent token acquisition failed:", error);

      if (error instanceof InteractionRequiredAuthError) {
        console.log("[MSAL] Interaction required - session needs re-authentication");

        // Clear our session data since we need to re-auth
        clearAuthSession();

        // For redirect flow, just throw - let checkAuth/checkError handle the redirect to login
        // Don't call acquireTokenRedirect here as it causes race conditions
        throw new Error("Session expired - re-authentication required");
      }
      throw error;
    }
  };

  // Shared error handling via @aegle/ra-commons (#4265 AC#3):
  //  - 401: attempt a silent refresh; on failure clear the session and redirect to
  //    /login with "Session expired" (preserved behavior).
  //  - 403: notify the user and KEEP the session — previously this only logged a
  //    console.warn, so a denied user saw nothing (new, user-visible behavior).
  //  - 407: handled by the factory (proxy-auth).
  const raCheckError = createCheckError({
    onSilentRefresh: async () => {
      // forceRefresh so a server-rejected-but-locally-cached token is actually
      // re-fetched; otherwise a revoked token loops on 401 without logging out.
      await acquireTokenSilent(true);
    },
    onLogout: async () => {
      clearAuthSession();
    },
    on403: (apiError) => {
      // Surface the backend's specific reason (title + actionable details + code),
      // not a generic string — e.g. "Partner is read-only / Select a writable
      // partner…". `apiError.raw` is the normalized HttpError carrying the enriched
      // body. Fall back to a generic title only if the backend gave none.
      const rich = parseRichError((apiError as { raw?: unknown })?.raw ?? apiError);
      notifyImperative(rich.message || "You don't have permission to do that", {
        type: "warning",
        multiLine: true,
        autoHideDuration: 10000,
        _aegle: rich,
      } as Parameters<typeof notifyImperative>[1]);
    },
  });

  return {
    login: async (params: ValidAny) => {
      console.log("[AuthProvider] Login called with params:", params);
      try {
        // Ensure MSAL is initialized
        await ensureInitialized();

        // Use effective scopes (filtered for CIAM if needed)
        const loginScopes = effectiveScopes.length > 0 ? effectiveScopes : finalConfig.scopes!;

        console.log("[AuthProvider] Using login scopes:", loginScopes);

        const loginRequest: PopupRequest | RedirectRequest = {
          scopes: loginScopes,
          prompt: "select_account",
        };

        let response: AuthenticationResult | null = null;

        if (finalConfig.loginMethod === "redirect") {
          console.log("[AuthProvider] Initiating redirect login...");
          // For redirect flow, initiate the redirect
          await msalInstance.loginRedirect(loginRequest as RedirectRequest);
          // The page will redirect, so we won't reach here
          console.log("[AuthProvider] Redirect initiated, returning success");
          return Promise.resolve();
        } else {
          // Popup flow
          console.log("[AuthProvider] Initiating popup login...");
          response = await msalInstance.loginPopup(loginRequest as PopupRequest);
          console.log("[AuthProvider] Popup response:", response);
        }

        if (response && response.account) {
          console.log("[AuthProvider] Login successful, setting active account");
          // Store account info for quick access
          msalInstance.setActiveAccount(response.account);

          // Fetch backend user data and effective permissions from /auth/me
          await fetchAuthMeData(response.account);

          return Promise.resolve();
        }

        console.error("[AuthProvider] Login failed - no account returned");
        throw new Error("Login failed - no account returned");
      } catch (error: ValidAny) {
        console.error("[AuthProvider] Login error:", error);
        return Promise.reject(
          new HttpError("Login failed", API_CONSTANTS.STATUS_CODES.UNAUTHORIZED.CODE, {
            message: error?.message || "Authentication failed",
          })
        );
      }
    },

    logout: async () => {
      try {
        // Ensure MSAL is initialized
        await ensureInitialized();

        const account = getCurrentAccount();
        if (account) {
          const logoutRequest = {
            account: account,
            postLogoutRedirectUri: finalConfig.postLogoutRedirectUri,
          };

          // Clear stored auth session data
          clearAuthSession();

          if (finalConfig.loginMethod === "redirect") {
            await msalInstance.logoutRedirect(logoutRequest);
          } else {
            await msalInstance.logoutPopup(logoutRequest);
          }
        }
        return Promise.resolve();
      } catch (error) {
        console.error("Logout error:", error);
        return Promise.resolve(); // Still resolve to allow app to handle logout
      }
    },

    checkAuth: async () => {
      console.log("[AuthProvider] checkAuth called");
      try {
        await ensureInitialized();

        const account = getCurrentAccount();
        if (!account) {
          console.log("[AuthProvider] No account found, rejecting auth");

          // Save the current hash route so we can restore it after login redirect
          const currentHash = window.location.hash;
          if (currentHash && currentHash !== "#/" && currentHash !== "#/login") {
            sessionStorage.setItem(STORAGE_KEYS.AUTH_REDIRECT_URL, currentHash);
            console.log("[AuthProvider] Saved deep link for post-login redirect:", currentHash);
          }

          // No message = no notification
          return Promise.reject({ redirectTo: "/login" });
        }

        console.log("[AuthProvider] Attempting to acquire token silently...");
        await acquireTokenSilent();
        console.log("[AuthProvider] Token acquired successfully");

        return Promise.resolve();
      } catch (error) {
        console.error("[AuthProvider] checkAuth failed:", error);
        // No message = no notification
        return Promise.reject({
          redirectTo: "/login",
          message: "Your session has expired. Please sign in again.",
        });
      }
    },

    checkError: async (error: ValidAny) => {
      console.log("[AuthProvider] checkError called with:", error?.status);
      // MSAL must be initialized before onSilentRefresh (acquireTokenSilent) can run.
      await ensureInitialized();
      return raCheckError(error);
    },

    getPermissions: async () => {
      try {
        // Ensure MSAL is initialized
        await ensureInitialized();

        // Return backend effective permissions from /auth/me (stored during login)
        const raPermissions = sessionStorage.getItem(STORAGE_KEYS.RA_PERMISSIONS);
        if (raPermissions) {
          return JSON.parse(raPermissions);
        }

        return {};
      } catch (error) {
        console.error("Error getting permissions:", error);
        return {};
      }
    },

    getIdentity: async (params?: ValidAny): Promise<UserIdentity> => {
      console.log("[AuthProvider] Get Identity:", params);
      try {
        // Ensure MSAL is initialized
        await ensureInitialized();

        const account = getCurrentAccount();
        if (!account) {
          // Return a default/anonymous identity instead of null
          return {
            id: "anonymous",
            fullName: "Anonymous User",
          };
        }

        // Check for backend user data first
        const authMeData = sessionStorage.getItem(STORAGE_KEYS.AUTH_ME_DATA);
        if (authMeData) {
          const data = JSON.parse(authMeData);
          return {
            // Core identity fields
            id: data.user.id,
            fullName: data.user.full_name || data.user.preferred_name || account.name || "Unknown User",
            email: data.user.email || account.username,
            avatar: undefined,

            // Extended user fields
            aegleId: data.user.aegle_id,
            uniqueName: data.user.unique_name,
            userPrincipalName: data.user.user_principal_name,
            preferredName: data.user.preferred_name,
            phone: data.user.phone,
            department: data.user.department,
            jobTitle: data.user.job_title,
            managerUserIdRef: data.user.manager_user_id_ref,
            timezone: data.user.timezone,
            locale: data.user.locale,
            preferredLanguage: data.user.preferred_language,
            status: data.user.status,
            accountType: data.user.account_type,
            emailVerified: data.user.email_verified,
            phoneVerified: data.user.phone_verified,
            partnerId: data.user.partner_id_ref,

            // Role and permission data
            assignedRoles: data.user.assigned_roles || [],
            effectivePermissions: data.user.effective_permissions || [],

            // Metadata
            metadata: data.metadata,
          };
        }

        // Create the identity object with base UserIdentity properties
        const identity: UserIdentity = {
          id: account.localAccountId || account.homeAccountId,
          fullName: account.name || "Unknown User",
          avatar: undefined, // You can add logic to fetch user photo from MS Graph if needed
        };

        // Add email if available
        if (account.username) {
          identity.email = account.username;
        }

        return identity;
      } catch (error) {
        console.error("Error getting identity:", error);
        // Return a default identity on error instead of null
        return {
          id: "unknown",
          fullName: "Unknown User",
        };
      }
    },

    // Optional: Add method to handle redirect callback
    handleRedirectCallback: async () => {
      try {
        await ensureInitialized();
        // The redirect handling is now done in ensureInitialized
      } catch (error) {
        console.error("Error handling redirect:", error);
      }
    },

    // Helper method to get access token for API calls
    getAccessToken: async (): Promise<string | null> => {
      try {
        // Ensure MSAL is initialized
        await ensureInitialized();
        const response = await acquireTokenSilent();
        return response.accessToken;
      } catch (error) {
        console.error("Error getting access token:", error);
        return null;
      }
    },
  };
};

// Export the factory function as default
export default createMsalAuthProvider;
